import { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Grid, List, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { usePostValidateNextStageMutation } from "@api/applications";
import CenteredLoader from "@components/CenteredLoader";
import useStatusConfig from "@hooks/useStatusConfig";
import ListDocuments from "@components/ListDocuments";
import { CheckListSignatureDocument } from "@interfaces/index";

const SCREEN_STATUS = "CSFRL";

const FirmaDocumentosPF = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  // state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const checklistDocumentsToSign: CheckListSignatureDocument[] = [
    {
      order: 1,
      title: "Documentos Contractuales",
      documents: ["Contrato", "Tabla de amortización", "Autorización de domiciliación"],
    },
    {
      order: 2,
      title: "Pagaré",
      documents: [],
    },
  ];

  const handleContinue = async () => {
    try {
      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      setIsSubmitting(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "FirmaDocumentosPF-handleContinue",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id) {
        throw new Error("Application ID is required");
      }
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "FirmaDocumentosPF-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [application.id, dispatch]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <CenteredLoader />
          ) : (
            <>
              <Typography variant={"tofu_title"} textAlign="center" color={"primary.main"}>
                Firma de documentos
              </Typography>

              <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
                <Typography
                  sx={{ color: "primary.dark", mb: 1 }}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  textAlign="center"
                  fontWeight={500}
                >
                  En caso de que tengas alguna duda no dudes en contactar a tu asesor
                </Typography>

                <Box
                  mt={isMobileOrTablet ? 2 : 4}
                  sx={{
                    backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
                    borderRadius: "10px",
                    padding: "20px",
                    maxWidth: "98%",
                  }}
                >
                  <Typography
                    mt={isMobileOrTablet ? 2 : 1}
                    textAlign={"left"}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.dark}
                    fontWeight={600}
                  >
                    Se firmarán los siguientes documentos
                  </Typography>

                  <List sx={{ listStyleType: "disc", paddingLeft: 2 }}>
                    {checklistDocumentsToSign.map((item, index) => (
                      <Box sx={{ mt: index === 0 ? 0 : 1 }}>
                        <ListDocuments
                          key={item.order}
                          title={item.title}
                          documents={item.documents}
                          showLegend={true}
                        />
                      </Box>
                    ))}
                  </List>

                  <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 4 : 6}>
                    <Button
                      type="button"
                      variant="default"
                      disabled={isLoading || isSubmitting}
                      onClick={handleContinue}
                    >
                      Firmar documentos
                    </Button>
                  </Box>
                </Box>
              </Container>
            </>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentosPF;
